import { ColumnDef } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { CashPool } from '@/types/cash-pool.types';
import { formatDecimalParts, formatDecimalString } from '@/utils/decimal-display';

/**
 * Register columns. Fixed `size` per column, like the counterparty and custodian
 * registers - `CashPoolTable` lays the header and the rows out from it.
 *
 * `canManage` gates the edit affordance: pool writes need the `create_trades`
 * capability, so a viewer must not be offered a pencil that ends in a 403.
 */
export const createCashPoolColumns = (
  onEdit: (pool: CashPool) => void,
  canManage: boolean
): ColumnDef<CashPool>[] => {
  const columns: ColumnDef<CashPool>[] = [
    {
      accessorKey: 'name',
      header: 'Pool Name',
      size: 260,
      cell: ({ row }) => (
        <span className="font-medium text-gray-900">{row.original.name}</span>
      ),
    },
    {
      accessorKey: 'counterparty_name',
      header: 'Counterparty',
      size: 180,
    },
    {
      id: 'rate_definition',
      header: 'Rate Definition',
      size: 150,
      accessorFn: (row) => row.rate_definition,
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.rate_definition}</span>
      ),
    },
    {
      id: 'day_count',
      header: 'Accrual Days',
      size: 120,
      accessorFn: (row) => row.day_count_convention,
      cell: ({ row }) => `ACT/${row.original.day_count_convention}`,
    },
    {
      id: 'balance',
      header: 'Balance',
      size: 140,
      // Money reads right-aligned in every register table; `meta.align` is what
      // CashPoolTable feeds ColumnHeader, so the HEADER follows the cells.
      meta: {
        align: 'right' as const,
        // The figure counts a booking from the day it is BOOKED, so inside the
        // settlement window it legitimately differs from the statement - and
        // nothing on screen said so. Cheaper than restating the number, which
        // would then disagree with the deactivation guard reading the same total.
        headerTooltip: 'Committed cash: includes bookings that have not settled yet',
      },
      accessorFn: (row) => row.balance ?? '0.00',
      cell: ({ row }) => (
        // LR-586: what the trades in this pool are currently holding, committed
        // cash included (a booking counts from the moment it is made, not from
        // its settlement date). Formatted through the project's Decimal helper
        // (HON-03) - the backend figure is an authoritative Decimal string and
        // must not go through Number(). Right aligned and tabular so the column
        // scans as money; a zero pool reads '0.00' rather than a dash, because
        // "empty" is a real, checkable state (it is what lets the pool be
        // switched off). Negative is real too: a borrow posts the counterparty's
        // way, so the sign has to read at a glance.
        <span
          className={clsx(
            'block text-right tabular-nums',
            // Sign taken from the parsed parts, not `balance.startsWith('-')`:
            // decimal-display's own docs warn that a raw string method on a
            // decimal field crashes the whole table the moment the value arrives
            // as a number rather than a string.
            formatDecimalParts(row.original.balance ?? '0.00').sign === '-' && 'text-red-600'
          )}
        >
          {formatDecimalString(row.original.balance ?? '0.00')}
        </span>
      ),
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      size: 90,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      size: 100,
      cell: ({ row }) => (
        <span
          className={
            row.original.is_active
              ? 'inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800'
              : 'inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'
          }
        >
          {row.original.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  if (canManage) {
    columns.push({
      id: 'actions',
      header: '',
      size: 50,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => onEdit(row.original)}
          aria-label={`Edit ${row.original.name}`}
          className="rounded text-gray-400 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <PencilSquareIcon className="h-4 w-4" />
        </button>
      ),
    });
  }

  return columns;
};
